/* global HTMLDivElement, KeyboardEvent */
import { useEffect, useRef, useState } from "react";
import { Calendar, CheckCircle2, Clock, Inbox, SkipForward, X } from "lucide-react";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { InboxItem } from "../types";
import { todayIso } from "../lib/date";
import { parseQuickAdd } from "../lib/quick-add-parser";
import { useFocusTrap } from "../lib/focus-trap";
import { InboxClarifyModal } from "./inbox-clarify-modal";
import { WaitingModal } from "./inbox-waiting-modal";

type Props = {
  api: LifeOsApi;
  items: InboxItem[];
  onClose: () => void;
  onDone: () => void;
};

export function InboxProcessingModal({ api, items, onClose, onDone }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, true);
  const [index, setIndex] = useState(0);
  const [showClarify, setShowClarify] = useState(false);
  const [showWaiting, setShowWaiting] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const snapshotItem = items[index] ?? null;
  const item = snapshotItem ? api.state.inbox.find((entry) => entry.id === snapshotItem.id) ?? snapshotItem : null;

  function advance() {
    const next = index + 1;
    if (next >= items.length) {
      onDone();
      onClose();
      return;
    }
    setShowClarify(false);
    setShowWaiting(false);
    setIndex(next);
  }

  if (!item) {
    return null;
  }

  const currentItem = item;
  const parsed = parseQuickAdd(currentItem.text);
  const title = parsed.title || currentItem.text;
  const today = todayIso();

  function makeTodayTask() {
    api.actions.convertInboxItem(currentItem.id, {
      type: "task",
      item: {
        title,
        dueDate: parsed.dueDate ?? today,
        status: "todo",
        priority: parsed.priority ?? "medium",
        estimateMinutes: 30,
        tags: parsed.tags,
        subtasks: [],
      },
    });
    advance();
  }

  function makeCalendarEvent() {
    api.actions.convertInboxItem(currentItem.id, {
      type: "event",
      item: {
        title,
        date: parsed.dueDate ?? today,
        time: parsed.time ?? "09:00",
        color: "#7c3aed",
      },
    });
    advance();
  }

  function parkSomeday() {
    api.actions.updateInboxItem(currentItem.id, { status: "someday" });
    advance();
  }

  function archiveItem() {
    api.actions.remove("inbox", currentItem.id);
    advance();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="inbox-processing-title">
      <div ref={panelRef} className="w-full max-w-lg rounded-3xl bg-white p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="mb-4 flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-2xl bg-brand-100 text-brand-600">
            <Inbox className="h-5 w-5" />
          </span>
          <div>
            <h2 id="inbox-processing-title" className="font-display text-[18px] font-bold">Xử lý inbox</h2>
            <p className="text-[12px] text-ink-500">Mục {index + 1}/{items.length}</p>
          </div>
          <button onClick={onClose} aria-label="Đóng" className="ml-auto grid h-8 w-8 place-items-center rounded-xl text-ink-500 hover:bg-ink-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-4 rounded-2xl bg-ink-50 px-4 py-3">
          <p className="text-[15px] font-semibold leading-snug">{currentItem.text}</p>
          {parsed.tags.length > 0 && (
            <p className="mt-2 text-[11px] font-semibold text-brand-600">{parsed.tags.map((tag) => `#${tag}`).join(" ")}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button onClick={makeTodayTask} className="flex items-center gap-2 rounded-2xl bg-emerald-50 px-4 py-3 text-left text-[13px] font-bold text-emerald-700 hover:bg-emerald-100">
            <CheckCircle2 className="h-4 w-4" />
            Làm hôm nay
          </button>
          <button onClick={makeCalendarEvent} className="flex items-center gap-2 rounded-2xl bg-sky-50 px-4 py-3 text-left text-[13px] font-bold text-sky-700 hover:bg-sky-100">
            <Calendar className="h-4 w-4" />
            Lên lịch
          </button>
          <button onClick={parkSomeday} className="rounded-2xl bg-ink-100 px-4 py-3 text-left text-[13px] font-bold text-ink-700 hover:bg-ink-200">
            Someday
          </button>
          <button onClick={() => setShowWaiting(true)} className="flex items-center gap-2 rounded-2xl bg-amber-50 px-4 py-3 text-left text-[13px] font-bold text-amber-700 hover:bg-amber-100">
            <Clock className="h-4 w-4" />
            Đang chờ
          </button>
        </div>

        <div className="mt-4 flex flex-wrap justify-between gap-2 border-t border-ink-200 pt-4">
          <button onClick={() => setShowClarify(true)} className="gbrand rounded-xl px-4 py-2 text-[13px] font-bold text-white">
            Phân loại chi tiết
          </button>
          <div className="flex gap-2">
            <button onClick={advance} className="flex items-center gap-1.5 rounded-xl bg-ink-100 px-3 py-2 text-[13px] font-bold text-ink-600 hover:bg-ink-200">
              <SkipForward className="h-3.5 w-3.5" />
              Bỏ qua
            </button>
            <button onClick={archiveItem} className="rounded-xl bg-rose-50 px-3 py-2 text-[13px] font-bold text-rose-600 hover:bg-rose-100">
              Xoá
            </button>
          </div>
        </div>
      </div>

      {showClarify && (
        <InboxClarifyModal
          item={currentItem}
          api={api}
          onClose={() => setShowClarify(false)}
          queue={{ index: index + 1, total: items.length, onNext: advance }}
        />
      )}

      {showWaiting && (
        <WaitingModal
          onConfirm={(waitingFor, waitingUntil) => {
            api.actions.updateInboxItem(currentItem.id, {
              status: "waiting",
              ...(waitingFor.trim() ? { waitingFor: waitingFor.trim() } : {}),
              ...(waitingUntil ? { waitingUntil } : {}),
            });
            advance();
          }}
          onClose={() => setShowWaiting(false)}
        />
      )}
    </div>
  );
}
